import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { api, connectAuth } from '@/shared/api'

import type { ApiAuthResponse, LoginCredentials, Role, User } from '../types'

const HUMAN_ACTIVITY_SYNC_INTERVAL_MS = 5 * 60_000
let refreshInFlight: Promise<void> | null = null
let humanActivitySyncTimer: number | null = null

export function parseAuthSession(response: ApiAuthResponse): { user: User; token: string } {
  const roles = response.user.roles
  if (
    roles.length !== 1 ||
    !(['admin', 'seller_agent', 'accountant'] as const).includes(roles[0] as Role)
  ) {
    throw new Error('La sesión no contiene exactamente un rol canónico.')
  }
  return {
    user: {
      ...response.user,
      role: roles[0] as Role,
      mustChangePassword: response.user.mustChangePassword ?? false,
    },
    token: response.accessToken,
  }
}

/**
 * =============================================================================
 * ESTADO DE SESIÓN
 * =============================================================================
 * Este es CLIENT STATE: información que vive en el navegador, no en el backend.
 * Por eso usa Zustand y no TanStack Query. La distinción está explicada en
 * docs/03-capa-de-datos.md, y es la decisión que más se equivoca al empezar.
 *
 * Regla rápida:
 *   ¿El dato es del servidor y puede quedar desactualizado?  → TanStack Query
 *   ¿El dato solo existe en esta pestaña del navegador?      → Zustand
 * =============================================================================
 */

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean
  sessionStartedAt: number | null
  lastHumanActivityAt: number | null
  lastHumanActivitySyncedAt: number | null

  signIn: (credentials: LoginCredentials) => Promise<User>
  refreshSession: () => Promise<void>
  canRefreshSession: () => boolean
  signOut: () => void
  markRequiredPasswordChanged: () => void
}

export const useAuthStore = create<AuthState>()(
  /**
   * `persist` guarda el estado para que la sesión sobreviva a un F5.
   *
   * ⚠️ DECISIÓN DE SEGURIDAD PENDIENTE
   * Guardar el token en localStorage es cómodo pero vulnerable a XSS: si un
   * atacante logra ejecutar JavaScript en la página (por ejemplo a través de
   * una librería comprometida), puede leer localStorage y robar el token.
   *
   * La alternativa más segura es que el backend envíe el token en una cookie
   * `httpOnly` + `Secure` + `SameSite=Strict`: el JavaScript no puede leerla,
   * el navegador la manda sola, y el frontend deja de tocar el token.
   *
   * Cuál usar depende de cómo lo implemente el backend. Cuando se sepa, se
   * decide y se ajusta AQUÍ y en `withCredentials` del httpClient.
   * Ver docs/04-permisos.md, sección "Dónde vive el token".
   */
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      sessionStartedAt: null,
      lastHumanActivityAt: null,
      lastHumanActivitySyncedAt: null,

      signIn: async (credentials) => {
        set({ isLoading: true })

        try {
          const response = await api.post<ApiAuthResponse>('/auth/login', credentials)
          const session = parseAuthSession(response)

          const now = Date.now()
          set({
            user: session.user,
            token: session.token,
            refreshToken: response.refreshToken,
            isLoading: false,
            sessionStartedAt: now,
            lastHumanActivityAt: now,
            lastHumanActivitySyncedAt: now,
          })

          return session.user
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      refreshSession: async () => {
        if (refreshInFlight) return refreshInFlight

        refreshInFlight = (async () => {
          const state = get()
          if ((state.sessionStartedAt || state.lastHumanActivityAt) && !state.canRefreshSession()) {
            throw new Error('La sesión expiró por inactividad o antigüedad máxima.')
          }
          const refreshToken = state.refreshToken
          if (!refreshToken) {
            set({ user: null, token: null, refreshToken: null })
            throw new Error('No hay una sesión para actualizar.')
          }

          const activityAt = state.lastHumanActivityAt
          const response = await api.post<ApiAuthResponse>('/auth/refresh', {
            refreshToken,
            ...(activityAt ? { humanActivityAt: new Date(activityAt).toISOString() } : {}),
          })
          const session = parseAuthSession(response)
          set((current) => ({
            user: session.user,
            token: session.token,
            refreshToken: response.refreshToken,
            ...(activityAt
              ? {
                  lastHumanActivitySyncedAt: Math.max(
                    current.lastHumanActivitySyncedAt ?? 0,
                    activityAt,
                  ),
                }
              : {}),
          }))
        })().finally(() => {
          refreshInFlight = null
        })

        return refreshInFlight
      },

      signOut: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          sessionStartedAt: null,
          lastHumanActivityAt: null,
          lastHumanActivitySyncedAt: null,
        })
        if (humanActivitySyncTimer !== null && typeof window !== 'undefined') {
          window.clearTimeout(humanActivitySyncTimer)
          humanActivitySyncTimer = null
        }
      },

      canRefreshSession: () => {
        const state = get()
        if (!state.user) return false
        if (!state.sessionStartedAt || !state.lastHumanActivityAt) return true
        const now = Date.now()
        return (
          now - state.sessionStartedAt < 5 * 60 * 60_000 &&
          now - state.lastHumanActivityAt < 60 * 60_000
        )
      },

      markRequiredPasswordChanged: () => {
        set((state) => ({
          user: state.user ? { ...state.user, mustChangePassword: false } : null,
        }))
      },
    }),
    {
      name: 'alto-palermo-auth',
      // Solo se persiste lo necesario. `isLoading` es un estado momentáneo:
      // guardarlo dejaría la app pensando que está cargando al recargar.
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        sessionStartedAt: state.sessionStartedAt,
        lastHumanActivityAt: state.lastHumanActivityAt,
      }),
    },
  ),
)

/**
 * Conecta la sesión con el cliente HTTP.
 *
 * Esta es la otra mitad de la inversión de dependencias explicada en
 * `shared/api/authBridge.ts`: la capa `shared` definió el hueco, y aquí la
 * feature `auth` lo rellena. Se ejecuta una sola vez, al cargar este módulo.
 *
 * Resultado: el cliente HTTP puede usar el token sin importar nada de
 * `features/`, respetando la regla de dependencias del proyecto.
 */
connectAuth({
  getToken: () => useAuthStore.getState().token,
  onSessionExpired: () => {
    useAuthStore.getState().signOut()
  },
  refreshSession: () => useAuthStore.getState().refreshSession(),
  canRefreshSession: () => useAuthStore.getState().canRefreshSession(),
})

if (typeof window !== 'undefined') {
  const scheduleHumanActivitySync = (): void => {
    if (humanActivitySyncTimer !== null) return

    humanActivitySyncTimer = window.setTimeout(async () => {
      humanActivitySyncTimer = null
      const state = useAuthStore.getState()
      if (!state.user || !state.lastHumanActivityAt) return
      if (!state.canRefreshSession()) {
        state.signOut()
        return
      }

      const activityAt = state.lastHumanActivityAt
      if (activityAt <= (state.lastHumanActivitySyncedAt ?? 0)) return

      try {
        await state.refreshSession()
      } catch {
        // A later human event may retry; network failures must not cause an
        // unexpected logout from the background activity synchronizer.
        return
      }

      const current = useAuthStore.getState()
      if (
        current.user &&
        current.lastHumanActivityAt &&
        current.lastHumanActivityAt > (current.lastHumanActivitySyncedAt ?? 0)
      ) {
        scheduleHumanActivitySync()
      }
    }, HUMAN_ACTIVITY_SYNC_INTERVAL_MS)
  }

  const recordHumanActivity = () => {
    const state = useAuthStore.getState()
    if (!state.user) return

    useAuthStore.setState({ lastHumanActivityAt: Date.now() })
    scheduleHumanActivitySync()
  }

  for (const eventName of ['pointerdown', 'keydown', 'touchstart']) {
    window.addEventListener(eventName, recordHumanActivity, { passive: true })
  }

  window.setInterval(() => {
    const state = useAuthStore.getState()
    if (!state.user) return
    if (!state.canRefreshSession()) {
      state.signOut()
      return
    }
    const now = Date.now()
    const inactivityRemaining = 60 * 60_000 - (now - (state.lastHumanActivityAt ?? now))
    const absoluteRemaining = 5 * 60 * 60_000 - (now - (state.sessionStartedAt ?? now))
    const remaining = Math.min(inactivityRemaining, absoluteRemaining)
    if (remaining <= 5 * 60_000) {
      window.dispatchEvent(
        new CustomEvent('auth-session-expiring', {
          detail: {
            remainingMs: remaining,
            reason: inactivityRemaining <= absoluteRemaining ? 'inactivity' : 'absolute',
          },
        }),
      )
    }
  }, 30_000)
}
